import { gql, TypedDocumentNode } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { FormProvider, useForm } from '@repo/shared-client/form'
import { useTranslation } from '@web/src/lib/intl/react'
import { ArrowLeftRightIcon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'
import SFProfileSearchField from 'src/components/primatives/sf-profile-search-field/sf-profile-search-field'
import { PublicationInfo } from 'src/lib/fragments'
import type { TransferPublicationMutation, TransferPublicationMutationVariables } from 'types/graphql'

const TRANSFER_PUBLICATION_MUTATION: TypedDocumentNode<
  TransferPublicationMutation,
  TransferPublicationMutationVariables
> = gql`
  mutation TransferPublicationMutation($publicationIdOrSlug: String!, $input: UpdatePublicationInput!) {
    updatePublication(publicationIdOrSlug: $publicationIdOrSlug, input: $input) {
      ...PublicationInfo
    }
  }
  ${PublicationInfo}
`

type CustomModalProps = {
  open: boolean
  onOpenChange?: (details: { open: boolean }) => void
}

const PublicationTransferModal = ({
  publicationId,
  onComplete,
  ...props
}: CustomModalProps & {
  publicationId?: string
  onComplete?: () => void
}) => {
  const { t } = useTranslation()
  const formMethods = useForm()
  const [showSpinner, setShowSpinnerValue] = useState(false)
  const [profile, setProfile] = useState(null)

  const [transferPublication] = useMutation(TRANSFER_PUBLICATION_MUTATION, {
    onCompleted: () => {
      closeDialog()
      toast.success(t('Publication ownership transferred successfully'))
      onComplete?.()
    },
    onError: (error) => {
      toast.error(error.message)
      closeDialog()
    },
  })

  const onSubmit = () => {
    setShowSpinnerValue(true)
    transferPublication({
      variables: { publicationIdOrSlug: publicationId, input: { ownerId: profile?.id } },
    })
  }

  const closeDialog = () => {
    props?.onOpenChange?.({ open: false })
  }

  return (
    <ConfirmDialog
      beforeControls={
        <FormProvider {...formMethods}>
          <SFProfileSearchField
            excludeSelf={false}
            inputProps={{ placeholder: t('Search profiles here...') }}
            label={t('Select New Owner')}
            name="profile"
            onProfileSelected={(profileValue) => {
              setProfile(profileValue ?? null)
              return false
            }}
          />
        </FormProvider>
      }
      confirmationPlaceholder={t('Type confirm')}
      confirmationPrompt={t("Enter 'confirm' in the box below")}
      confirmationText="confirm"
      confirmDisabled={!profile}
      confirmText={t('Transfer')}
      icon={<ArrowLeftRightIcon />}
      loading={showSpinner}
      onConfirm={onSubmit}
      onOpenChange={(details) => {
        props?.onOpenChange?.(details)

        if (!details.open) {
          formMethods.reset()
          setShowSpinnerValue(false)
          setProfile(null)
        }
      }}
      open={props.open}
      text={t('You will no longer be able to edit this page if you transfer ownership.')}
      title={t('Transfer Publication Ownership')}
      variant="delete"
    />
  )
}

export default PublicationTransferModal
